import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { type LiveChatConversation, type LiveChatMessage, streamStatusLabels, billingStatusLabels } from '@/mocks/superAdminLiveSupport';
import { translateText, type TranslationDirection, TRANSLATION_LABELS } from '@/mocks/liveChatTranslator';

/* ─── Quick Replies ─── */
const QUICK_REPLIES_ES = [
  'Hola, ¿en qué puedo ayudarte?',
  'Déjame revisar tu cuenta.',
  'Ya lo revisé, todo está en orden.',
  'Necesito que me envíes una captura del error.',
  'Voy a escalar este caso al equipo técnico.',
  '¿Hay algo más en lo que pueda ayudarte?',
  'Gracias por contactarnos, ¡buen día!',
  'Tu caso fue resuelto. Cualquier cosa nos escribís de nuevo.',
  'El problema ya está identificado, estamos trabajando en la solución.',
  'Por favor verificá tu conexión de stream.',
];

const QUICK_REPLIES_EN = [
  'Hello, how can I help you?',
  'Let me check your account.',
  'I checked, everything looks good.',
  'I need you to send me a screenshot of the error.',
  'I will escalate this case to the technical team.',
  'Is there anything else I can help with?',
  'Thanks for reaching out, have a great day!',
  'Your case has been resolved. Feel free to reach out again.',
  'The issue has been identified, we are working on the solution.',
  'Please verify your stream connection.',
];

/* ─── Templates ─── */
interface Template {
  id: string;
  title: string;
  content: string;
  category: string;
}

const TEMPLATES: Template[] = [
  { id: 't1', title: 'Bienvenida', content: '¡Hola! Gracias por contactar a RadioFrecuenciaWeb. Soy Carlos del equipo de soporte. ¿En qué puedo ayudarte hoy?', category: 'Saludos' },
  { id: 't2', title: 'Verificación de cuenta', content: 'Déjame revisar tu cuenta. Dame un momento por favor mientras verifico tus datos en el sistema.', category: 'Soporte' },
  { id: 't3', title: 'Problema de stream', content: 'Entiendo que estás teniendo problemas con el stream. Voy a revisar el estado de tu servidor. Mientras tanto, ¿podrías confirmarme la URL de stream que estás usando?', category: 'Soporte' },
  { id: 't4', title: 'Cambio de plan', content: 'Claro, puedo ayudarte con el cambio de plan. Los upgrades se aplican de inmediato y solo pagás la diferencia proporcional. ¿A qué plan te gustaría cambiar?', category: 'Facturación' },
  { id: 't5', title: 'Problema de pago', content: 'Veo que tuviste un problema con el pago. Voy a revisar el estado de tu facturación. ¿Podrías confirmarme el método de pago que usaste?', category: 'Facturación' },
  { id: 't6', title: 'Storage lleno', content: 'Revisé tu almacenamiento. Actualmente estás usando X de Y GB. Si necesitás más espacio podemos evaluar un upgrade de plan o la compra de storage adicional.', category: 'Soporte' },
  { id: 't7', title: 'Caso resuelto', content: '¡Listo! El problema quedó resuelto. ¿Hay algo más en lo que pueda ayudarte? Si no, voy a cerrar este chat. ¡Gracias por confiar en RFW!', category: 'Cierre' },
  { id: 't8', title: 'Escalamiento', content: 'Este caso requiere revisión de nuestro equipo técnico. Ya lo escalé con prioridad y te van a contactar en las próximas 24 horas hábiles. Tu número de caso es', category: 'Cierre' },
];

/* ─── Agent options ─── */
const agentOptions = [
  { id: 'SA-001', name: 'Carlos Mendoza' },
  { id: 'SA-002', name: 'Ana López' },
  { id: 'SA-003', name: 'Andrés García' },
  { id: 'SA-004', name: 'Diana Ríos' },
];

/* ─── Props ─── */
interface ChatAreaProps {
  conversation: LiveChatConversation | null;
  chatInput: string;
  onChatInputChange: (v: string) => void;
  onSend: (content: string, translateMode: TranslationDirection | 'off') => void;
  onResolve: () => void;
  onTransfer: (agentId: string) => void;
  onCreateTicket: () => void;
}

export default function ChatArea({
  conversation,
  chatInput,
  onChatInputChange,
  onSend,
  onResolve,
  onTransfer,
  onCreateTicket,
}: ChatAreaProps) {
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [translateMode, setTranslateMode] = useState<TranslationDirection | 'off'>('off');
  const [translatedPreview, setTranslatedPreview] = useState('');
  const [showTransferMenu, setShowTransferMenu] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; size: string }[]>([]);

  /* ─── Scroll to bottom on new messages ─── */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages]);

  /* ─── Translation preview ─── */
  useEffect(() => {
    if (translateMode === 'off' || !chatInput.trim()) {
      setTranslatedPreview('');
      return;
    }
    const result = translateText(chatInput.trim(), translateMode);
    setTranslatedPreview(result.translated === result.original ? '' : result.translated);
  }, [chatInput, translateMode]);

  /* ─── Handlers ─── */
  const handleSend = () => {
    if (!chatInput.trim() || !conversation) return;
    onSend(chatInput.trim(), translateMode);
    onChatInputChange('');
    setTranslatedPreview('');
    setShowQuickReplies(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickReply = (text: string) => {
    onChatInputChange(text);
    setShowQuickReplies(false);
  };

  const handleTemplateSelect = (template: Template) => {
    onChatInputChange(template.content);
    setShowTemplates(false);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files).map((f) => ({
      name: f.name,
      size: f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(1)} KB` : `${(f.size / (1024 * 1024)).toFixed(1)} MB`,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  /* ─── Empty state ─── */
  if (!conversation) {
    return (
      <div className="flex-1 bg-background-50 border border-background-200 rounded-2xl overflow-hidden flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-chat-3-line text-foreground-300 text-2xl"></i>
          </div>
          <p className="text-foreground-500 text-sm font-bold">Seleccioná una conversación</p>
          <p className="text-foreground-400 text-xs mt-1">Elegí una conversación de la lista para empezar a chatear.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-background-50 border border-background-200 rounded-2xl overflow-hidden flex flex-col min-w-0">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-background-200 shrink-0 gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-full bg-foreground-950 flex items-center justify-center shrink-0">
            <span className="text-background-50 text-[9px] font-bold">
              {conversation.radioName.slice(0, 2).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-foreground-800 text-xs font-semibold truncate">{conversation.radioName}</p>
              {conversation.transferredFromTicket && (
                <Link
                  to="/sa/support"
                  className="text-amber-500 hover:text-amber-600 transition-colors cursor-pointer flex items-center gap-0.5 shrink-0"
                  title={`Ver ticket ${conversation.transferredFromTicket}`}
                >
                  <i className="ri-ticket-2-line text-[10px]"></i>
                  <span className="text-[9px] font-semibold">{conversation.transferredFromTicket}</span>
                </Link>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <span className="text-foreground-400 text-[10px]">{conversation.frequency}</span>
              {(() => {
                const s = streamStatusLabels[conversation.streamStatus];
                return (
                  <span className={`px-1.5 py-0.5 rounded-md text-[8px] font-medium flex items-center gap-1 ${s.bg} ${s.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
                    {s.label}
                  </span>
                );
              })()}
              {(() => {
                const b = billingStatusLabels[conversation.billingStatus];
                return (
                  <span className={`px-1.5 py-0.5 rounded-md text-[8px] font-medium ${b.bg} ${b.color}`}>
                    {b.label}
                  </span>
                );
              })()}
              <span className="px-1.5 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[8px] font-medium">
                {conversation.planName}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {conversation.online ? (
            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-medium flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
              Online
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-400 text-[10px] font-medium">
              Offline
            </span>
          )}

          <button
            onClick={onCreateTicket}
            className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center cursor-pointer hover:bg-amber-100 transition-colors"
            title="Crear ticket"
          >
            <i className="ri-ticket-2-line text-amber-500 text-xs"></i>
          </button>

          {/* Transfer */}
          <div className="relative">
            <button
              onClick={() => setShowTransferMenu(!showTransferMenu)}
              className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              title="Transferir"
            >
              <i className="ri-share-forward-line text-foreground-500 text-xs"></i>
            </button>
            {showTransferMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowTransferMenu(false)}></div>
                <div className="absolute top-full right-0 mt-1 z-20 w-44 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1">
                  <p className="px-3 py-1.5 text-foreground-400 text-[9px] font-semibold uppercase">
                    Transferir a
                  </p>
                  {agentOptions
                    .filter((a) => a.name !== 'Carlos Mendoza')
                    .map((a) => (
                      <button
                        key={a.id}
                        onClick={() => { onTransfer(a.id); setShowTransferMenu(false); }}
                        className="w-full text-left px-3 py-2 text-[11px] font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        {a.name}
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>

          <button
            onClick={onResolve}
            className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100 transition-colors"
            title="Marcar resuelto"
          >
            <i className="ri-check-double-line text-emerald-500 text-xs"></i>
          </button>
        </div>
      </div>

      {/* ─── Messages ─── */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {conversation.transferredFromTicket && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-[10px] font-medium text-amber-700">
            <i className="ri-ticket-2-line text-sm"></i>
            <span>
              Conversación iniciada desde el ticket <strong>{conversation.transferredFromTicket}</strong>
            </span>
          </div>
        )}
        {conversation.messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.from === 'sa' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl ${
                msg.from === 'sa'
                  ? 'bg-foreground-950 text-background-50 rounded-br-md'
                  : 'bg-background-100 text-foreground-800 rounded-bl-md'
              }`}
            >
              <p className="text-[11px] font-medium">
                {msg.from === 'sa' ? 'Vos' : msg.author}
              </p>
              <p className="text-[11px] leading-relaxed mt-0.5 whitespace-pre-wrap break-words">
                {msg.content}
              </p>
              {msg.originalContent && (
                <div className={`mt-2 pt-2 border-t ${msg.from === 'sa' ? 'border-background-50/20' : 'border-foreground-200'}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <i className="ri-translate-2 text-[10px]"></i>
                    <span className={`text-[8px] font-semibold uppercase ${msg.from === 'sa' ? 'text-background-50/60' : 'text-foreground-400'}`}>
                      {msg.sourceLang} → {msg.targetLang}
                    </span>
                  </div>
                  <p className={`text-[10px] italic leading-relaxed ${msg.from === 'sa' ? 'text-background-50/70' : 'text-foreground-500'}`}>
                    {msg.originalContent}
                  </p>
                </div>
              )}
              <p
                className={`text-[9px] mt-1 ${
                  msg.from === 'sa' ? 'text-background-50/60' : 'text-foreground-400'
                }`}
              >
                {msg.timestamp}
              </p>
            </div>
          </div>
        ))}
        <div ref={chatEndRef}></div>
      </div>

      {/* ─── Uploaded files indicator ─── */}
      {uploadedFiles.length > 0 && (
        <div className="px-4 pb-1 space-y-1">
          {uploadedFiles.map((f, idx) => (
            <div key={idx} className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-background-100 border border-background-200">
              <div className="flex items-center gap-2 min-w-0">
                <i className="ri-file-line text-foreground-400 text-xs shrink-0"></i>
                <span className="text-foreground-700 text-[10px] truncate">{f.name}</span>
                <span className="text-foreground-400 text-[9px] shrink-0">{f.size}</span>
              </div>
              <button
                onClick={() => removeFile(idx)}
                className="w-5 h-5 rounded-md flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
              >
                <i className="ri-close-line text-foreground-400 text-[10px]"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ─── Input Area ─── */}
      <div className="px-4 py-3 border-t border-background-200 shrink-0 space-y-2">
        {/* Translation toggle + quick actions bar */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Translator */}
          <div className="flex items-center bg-background-100 rounded-lg p-0.5">
            <button
              onClick={() => setTranslateMode('off')}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                translateMode === 'off' ? 'bg-foreground-950 text-background-50' : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              Off
            </button>
            <button
              onClick={() => setTranslateMode('es-to-en')}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                translateMode === 'es-to-en' ? 'bg-accent-500 text-white' : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              ES → EN
            </button>
            <button
              onClick={() => setTranslateMode('en-to-es')}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                translateMode === 'en-to-es' ? 'bg-accent-500 text-white' : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              EN → ES
            </button>
          </div>
          {translateMode !== 'off' && (
            <span className="text-foreground-300 text-[9px]">{TRANSLATION_LABELS[translateMode].label}</span>
          )}

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Quick Replies */}
          <div className="relative">
            <button
              onClick={() => { setShowQuickReplies(!showQuickReplies); setShowTemplates(false); }}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                showQuickReplies ? 'bg-accent-500 text-white' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
              title="Respuestas rápidas"
            >
              <i className="ri-flashlight-line text-xs"></i>
              Rápidas
            </button>
            {showQuickReplies && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowQuickReplies(false)}></div>
                <div className="absolute bottom-full right-0 mb-2 z-20 w-80 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden">
                  <div className="px-3 py-2 border-b border-background-200 flex items-center justify-between">
                    <span className="text-foreground-800 text-[10px] font-bold">Respuestas rápidas</span>
                    <span className="text-foreground-400 text-[8px]">{QUICK_REPLIES_ES.length} frases</span>
                  </div>
                  <div className="max-h-[240px] overflow-y-auto p-1">
                    {QUICK_REPLIES_ES.map((reply, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleQuickReply(reply)}
                        className="w-full text-left px-3 py-2 rounded-lg text-[10px] text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer leading-relaxed"
                      >
                        {reply}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Templates */}
          <div className="relative">
            <button
              onClick={() => { setShowTemplates(!showTemplates); setShowQuickReplies(false); }}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                showTemplates ? 'bg-accent-500 text-white' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
              title="Plantillas"
            >
              <i className="ri-file-copy-line text-xs"></i>
              Plantillas
            </button>
            {showTemplates && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowTemplates(false)}></div>
                <div className="absolute bottom-full right-0 mb-2 z-20 w-80 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden">
                  <div className="px-3 py-2 border-b border-background-200 flex items-center justify-between">
                    <span className="text-foreground-800 text-[10px] font-bold">Plantillas</span>
                    <span className="text-foreground-400 text-[8px]">{TEMPLATES.length} disponibles</span>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto p-1">
                    {TEMPLATES.map((tpl) => (
                      <button
                        key={tpl.id}
                        onClick={() => handleTemplateSelect(tpl)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-foreground-800 text-[10px] font-semibold">{tpl.title}</span>
                          <span className="px-1.5 py-0.5 rounded-md bg-background-200 text-foreground-400 text-[8px] font-medium">
                            {tpl.category}
                          </span>
                        </div>
                        <p className="text-foreground-400 text-[9px] leading-relaxed line-clamp-2">{tpl.content}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* File upload */}
          <button
            onClick={handleFileSelect}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap text-[10px] font-medium"
            title="Adjuntar archivo"
          >
            <i className="ri-attachment-2 text-xs"></i>
            Archivo
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Input row */}
        <div className="flex items-start gap-2">
          <div className="flex-1 space-y-1.5">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => onChatInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                translateMode !== 'off'
                  ? TRANSLATION_LABELS[translateMode].placeholder
                  : 'Escribí un mensaje...'
              }
              className={`w-full px-3 py-2.5 bg-background-100 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none transition-colors ${
                translateMode !== 'off' ? 'border-accent-300 focus:border-accent-400' : 'border-background-200 focus:border-foreground-300'
              }`}
            />
            {translatedPreview && (
              <div className="flex items-start gap-1.5 px-2.5 py-1.5 bg-accent-50 border border-accent-200 rounded-lg">
                <i className="ri-translate-2 text-accent-500 text-[11px] mt-px shrink-0"></i>
                <div className="min-w-0">
                  <p className="text-foreground-400 text-[9px] font-medium leading-tight">Se enviará como:</p>
                  <p className="text-accent-700 text-[10px] font-semibold leading-tight break-words">
                    {translatedPreview}
                  </p>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={handleSend}
            disabled={!chatInput.trim()}
            className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center cursor-pointer hover:bg-foreground-800 transition-colors disabled:opacity-40 shrink-0"
          >
            <i className="ri-send-plane-fill text-background-50 text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}